import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { differenceInDays, parseISO } from 'date-fns';
import {
  ArrowUpDown, ArrowUp, ArrowDown, ArrowRightLeft, ExternalLink,
  Settings2, Download, Copy, ChevronsUpDown, Check, X, FilterX, Paperclip,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuCheckboxItem,
  DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import { formatCurrency, formatDate } from '@/lib/utils';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import { InvoiceStatusBadge } from './InvoiceStatusBadge';
import { escapeCSVValue } from '../utils/csvExport';
import type { Invoice, InvoicesFilters as InvoicesFiltersType, InvoiceStatus } from '../types';
import {
  TAX_CATEGORY_OPTIONS,
  getCustomerName,
  getContractDisplay,
  getUserName,
  isPopulatedOrganisation,
} from '../types';

// =============================================================================
// Column Definition
// =============================================================================

interface FilterOption {
  value: string;
  label: string;
}

export interface InvoiceColumnDef {
  key: string;
  label: string;
  defaultVisible?: boolean;
  sortKey?: InvoicesFiltersType['sortKey'];
  filterType?: 'text' | 'date-range' | 'select' | 'number-range' | 'searchable-select' | 'single-select';
  filterOptions?: FilterOption[];
  getValue: (invoice: Invoice) => string | number | null;
  align?: 'left' | 'center' | 'right';
  noFilter?: boolean;
  headerClassName?: string;
  cellClassName?: string;
}

// =============================================================================
// Filter option constants
// =============================================================================

const PAYMENT_TERMS_OPTIONS: FilterOption[] = [
  { value: 'on-order', label: 'On Order' },
  { value: 'net-0', label: 'Net 0' },
  { value: 'net-30', label: 'Net 30' },
  { value: 'net-45', label: 'Net 45' },
  { value: 'net-60', label: 'Net 60' },
];

// =============================================================================
// Column definitions
// =============================================================================

export const COLUMNS: InvoiceColumnDef[] = [
  {
    key: 'invoiceNumber',
    label: 'Invoice #',
    defaultVisible: true,
    sortKey: 'invoice_number',
    filterType: 'text',
    getValue: (inv) => inv.invoice_number,
    headerClassName: 'w-28',
    cellClassName: 'font-medium',
  },
  {
    key: 'status',
    label: 'Status',
    defaultVisible: true,
    filterType: 'select',
    filterOptions: [
      { value: 'preparation', label: 'Prep' },
      { value: 'sent', label: 'Sent' },
      { value: 'paid', label: 'Paid' },
      { value: 'expired', label: 'Expired' },
      { value: 'cancelled', label: 'Cancelled' },
    ],
    getValue: (inv) => inv.status,
    headerClassName: 'w-28',
  },
  {
    key: 'date',
    label: 'Issue Date',
    defaultVisible: true,
    sortKey: 'date',
    filterType: 'date-range',
    getValue: (inv) => inv.date || null,
    headerClassName: 'w-28',
  },
  {
    key: 'expirationDate',
    label: 'Due Date',
    defaultVisible: true,
    sortKey: 'expiration_date',
    filterType: 'date-range',
    getValue: (inv) => inv.expiration_date || null,
    headerClassName: 'w-28',
  },
  {
    key: 'paymentDate',
    label: 'Paid Date',
    defaultVisible: true,
    sortKey: 'payment_date',
    filterType: 'date-range',
    getValue: (inv) => inv.payment_date || null,
    headerClassName: 'w-28',
  },
  {
    key: 'account',
    label: 'Account',
    defaultVisible: true,
    sortKey: 'customer',
    filterType: 'searchable-select',
    getValue: (inv) => getCustomerName(inv.customer),
    cellClassName: 'max-w-40 truncate',
  },
  {
    key: 'project',
    label: 'Project',
    defaultVisible: true,
    filterType: 'text',
    getValue: (inv) => inv.project_name || null,
    cellClassName: 'max-w-40 truncate',
  },
  {
    key: 'salesManager',
    label: 'Account Manager',
    defaultVisible: true,
    filterType: 'single-select',
    getValue: (inv) => getUserName(inv.sales_manager),
    cellClassName: 'text-muted-foreground text-sm',
  },
  {
    key: 'total',
    label: 'Total',
    defaultVisible: true,
    sortKey: 'total',
    filterType: 'number-range',
    getValue: (inv) => inv.total ?? 0,
    align: 'right',
    headerClassName: 'w-28',
    cellClassName: 'font-medium',
  },
  {
    key: 'documents',
    label: 'Docs',
    defaultVisible: true,
    noFilter: true,
    getValue: (inv) => inv.document_count ?? 0,
    headerClassName: 'w-16',
    align: 'center',
  },
  // --- Hidden by default ---
  {
    key: 'createdAt',
    label: 'Created',
    defaultVisible: false,
    sortKey: 'created_at',
    filterType: 'date-range',
    getValue: (inv) => inv.created_at || null,
    headerClassName: 'w-28',
    cellClassName: 'text-muted-foreground text-sm',
  },
  {
    key: 'contract',
    label: 'Subscription',
    defaultVisible: false,
    filterType: 'text',
    getValue: (inv) => getContractDisplay(inv.contract),
    cellClassName: 'max-w-32 truncate text-muted-foreground text-sm',
  },
  {
    key: 'subtotal',
    label: 'Before Tax',
    defaultVisible: false,
    noFilter: true,
    getValue: (inv) => inv.subtotal_amount ?? inv.total ?? 0,
    align: 'right',
    cellClassName: 'text-sm',
  },
  {
    key: 'tax',
    label: 'Taxes',
    defaultVisible: false,
    noFilter: true,
    getValue: (inv) => inv.tax_amount ?? 0,
    align: 'right',
    cellClassName: 'text-sm',
  },
  {
    key: 'poNumber',
    label: 'P/O Number',
    defaultVisible: false,
    filterType: 'text',
    getValue: (inv) => inv.po_number || null,
    cellClassName: 'text-sm',
  },
  {
    key: 'quoteNumber',
    label: 'Quote Number',
    defaultVisible: false,
    filterType: 'text',
    getValue: (inv) => inv.quote_number || null,
    cellClassName: 'text-sm',
  },
  {
    key: 'currency',
    label: 'Currency',
    defaultVisible: false,
    filterType: 'text',
    getValue: (inv) => inv.currency || 'CAD',
    cellClassName: 'text-sm',
  },
  {
    key: 'paymentTerms',
    label: 'Payment Terms',
    defaultVisible: false,
    filterType: 'select',
    filterOptions: PAYMENT_TERMS_OPTIONS,
    getValue: (inv) => inv.payment_terms || null,
    cellClassName: 'text-sm',
  },
  {
    key: 'taxCategory',
    label: 'Tax Category',
    defaultVisible: false,
    filterType: 'select',
    filterOptions: TAX_CATEGORY_OPTIONS,
    getValue: (inv) => inv.tax_category || null,
    cellClassName: 'text-sm',
  },
  {
    key: 'contractualManager',
    label: 'Contractual Manager',
    defaultVisible: false,
    filterType: 'text',
    getValue: (inv) => getUserName(inv.contractual_manager),
    cellClassName: 'text-muted-foreground text-sm',
  },
  {
    key: 'trackingNumbers',
    label: 'Tracking #',
    defaultVisible: false,
    filterType: 'text',
    getValue: (inv) => (inv.tracking_numbers || []).join(', ') || null,
    cellClassName: 'text-sm',
  },
  {
    key: 'nextStep',
    label: 'Next Step',
    defaultVisible: false,
    filterType: 'text',
    getValue: (inv) => inv.next_step || null,
    cellClassName: 'text-sm max-w-[200px] truncate',
  },
  {
    key: 'nextStepDeadline',
    label: 'Next Step Deadline',
    defaultVisible: false,
    filterType: 'date-range',
    getValue: (inv) => inv.next_step_deadline || null,
    headerClassName: 'w-28',
    cellClassName: 'text-sm',
  },
  {
    key: 'actions',
    label: '',
    defaultVisible: true,
    noFilter: true,
    getValue: () => '',
    headerClassName: 'w-28',
  },
];

// =============================================================================
// Column visibility persistence
// =============================================================================

const VISIBILITY_STORAGE_KEY = 'atek-invoices-column-visibility';

function getDefaultVisibility(): Record<string, boolean> {
  const defaults: Record<string, boolean> = {};
  for (const col of COLUMNS) {
    defaults[col.key] = col.defaultVisible !== false;
  }
  return defaults;
}

function loadColumnVisibility(): Record<string, boolean> {
  try {
    const stored = localStorage.getItem(VISIBILITY_STORAGE_KEY);
    if (stored) return { ...getDefaultVisibility(), ...JSON.parse(stored) };
  } catch { /* ignore */ }
  return getDefaultVisibility();
}

function saveColumnVisibility(visibility: Record<string, boolean>) {
  localStorage.setItem(VISIBILITY_STORAGE_KEY, JSON.stringify(visibility));
}

// =============================================================================
// SortableHeader
// =============================================================================

function SortableHeader({
  label,
  sortKey,
  currentSortKey,
  sortDirection,
  onSort,
}: {
  label: string;
  sortKey: InvoicesFiltersType['sortKey'];
  currentSortKey: InvoicesFiltersType['sortKey'];
  sortDirection: 1 | -1;
  onSort: (key: InvoicesFiltersType['sortKey']) => void;
}) {
  const isActive = currentSortKey === sortKey;
  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 hover:bg-transparent"
      onClick={() => onSort(sortKey)}
    >
      {label}
      {isActive ? (
        sortDirection === 1 ? <ArrowUp className="ml-1 h-3 w-3" /> : <ArrowDown className="ml-1 h-3 w-3" />
      ) : (
        <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />
      )}
    </Button>
  );
}

// =============================================================================
// SearchableSelectFilter (Combobox with search + multi-select)
// =============================================================================

function SearchableSelectFilter({
  colKey,
  options,
  value,
  onChange,
}: {
  colKey: string;
  options: FilterOption[];
  value: string;
  onChange: (key: string, value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selected: string[] = useMemo(() => {
    try { return value ? JSON.parse(value) : []; }
    catch { return []; }
  }, [value]);

  const filtered = useMemo(() => {
    const base = search
      ? options.filter((o) => (o.label ?? o.value).toLowerCase().includes(search.toLowerCase()))
      : options;
    if (selected.length === 0) return base;
    const sel = new Set(selected);
    return [...base].sort((a, b) => (sel.has(a.value) ? 0 : 1) - (sel.has(b.value) ? 0 : 1));
  }, [options, search, selected]);

  const toggle = (opt: string) => {
    const next = selected.includes(opt)
      ? selected.filter((o) => o !== opt)
      : [...selected, opt];
    onChange(colKey, next.length ? JSON.stringify(next) : '');
  };

  const selectedLabel = useMemo(() => {
    if (selected.length === 0) return null;
    if (selected.length === 1) {
      const match = options.find((o) => o.value === selected[0]);
      return match?.label || '1 selected';
    }
    return `${selected.length} selected`;
  }, [selected, options]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="relative">
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="h-7 w-full text-xs justify-between font-normal pr-6"
          >
            <span className="truncate">{selectedLabel || '\u2014'}</span>
            <ChevronsUpDown className="h-3 w-3 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        {selected.length > 0 && (
          <button
            type="button"
            className="absolute right-1.5 top-1/2 -translate-y-1/2 z-10"
            onClick={() => { onChange(colKey, ''); setOpen(false); }}
          >
            <X className="h-3 w-3 opacity-50 hover:opacity-100" />
          </button>
        )}
      </div>
      <PopoverContent className="w-56 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search..."
            value={search}
            onValueChange={setSearch}
            className="h-8 text-xs"
          />
          <CommandList>
            <CommandEmpty className="py-2 text-center text-xs text-muted-foreground">
              No results
            </CommandEmpty>
            <CommandGroup className="max-h-48 overflow-y-auto">
              {filtered.map((opt) => (
                <CommandItem
                  key={opt.value}
                  value={opt.value}
                  onSelect={() => toggle(opt.value)}
                  className="text-xs"
                >
                  <div
                    className={cn(
                      'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                      selected.includes(opt.value)
                        ? 'bg-primary text-primary-foreground'
                        : 'opacity-50',
                    )}
                  >
                    {selected.includes(opt.value) && <Check className="h-3 w-3" />}
                  </div>
                  {opt.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// =============================================================================
// Helpers
// =============================================================================

function getDeadlineClass(expirationDate: string | undefined, status: InvoiceStatus): string {
  if (!expirationDate || status === 'paid' || status === 'cancelled') return '';
  try {
    const diff = differenceInDays(parseISO(expirationDate), new Date());
    if (diff < 0) return 'text-red-600 font-medium';
    if (diff <= 7) return 'text-yellow-600';
    return '';
  } catch {
    return '';
  }
}

// =============================================================================
// InvoicesTable
// =============================================================================

interface InvoicesTableProps {
  invoices: Invoice[];
  pageItemCount: number;
  total: number;
  sortKey: InvoicesFiltersType['sortKey'];
  sortDirection: 1 | -1;
  onSort: (key: InvoicesFiltersType['sortKey']) => void;
  onEditClick: (invoice: Invoice) => void;
  onReassign?: (invoice: Invoice) => void;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  columnFilters: Record<string, string>;
  onColumnFilterChange: (key: string, value: string) => void;
  onClearColumnFilters?: () => void;
  isLoading?: boolean;
  searchableSelectOptions?: Record<string, FilterOption[]>;
}

export function InvoicesTable({
  invoices,
  pageItemCount,
  total,
  sortKey,
  sortDirection,
  onSort,
  onEditClick,
  onReassign,
  page,
  pageSize,
  onPageChange,
  columnFilters,
  onColumnFilterChange,
  onClearColumnFilters,
  isLoading = false,
  searchableSelectOptions,
}: InvoicesTableProps) {
  const totalPages = Math.ceil(total / pageSize) || 1;
  const hasActiveColumnFilters = Object.values(columnFilters).some(Boolean);

  // ---------------------------------------------------------------------------
  // Column visibility
  // ---------------------------------------------------------------------------
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(loadColumnVisibility);

  const toggleColumn = (key: string) => {
    const updated = { ...columnVisibility, [key]: !columnVisibility[key] };
    setColumnVisibility(updated);
    saveColumnVisibility(updated);
  };

  const isVisible = (key: string) => columnVisibility[key] !== false;

  const visibleColumns = useMemo(
    () => COLUMNS.filter((col) => isVisible(col.key)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnVisibility],
  );

  // ---------------------------------------------------------------------------
  // Export handlers
  // ---------------------------------------------------------------------------
  const handleCopy = () => {
    const exportCols = visibleColumns.filter((c) => !c.noFilter);
    const header = exportCols.map((c) => c.label).join('\t');
    const rows = invoices.map((inv) =>
      exportCols.map((c) => String(c.getValue(inv) ?? '')).join('\t'),
    );
    navigator.clipboard.writeText([header, ...rows].join('\n'));
    toast.success('Copied to clipboard');
  };

  const handleCSV = () => {
    const exportCols = visibleColumns.filter((c) => !c.noFilter);
    const header = exportCols.map((c) => escapeCSVValue(c.label)).join(',');
    const rows = invoices.map((inv) =>
      exportCols.map((c) => escapeCSVValue(c.getValue(inv))).join(','),
    );
    const csvContent = [header, ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `invoices_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${invoices.length} rows`);
  };

  // ---------------------------------------------------------------------------
  // Column filter rendering
  // ---------------------------------------------------------------------------
  const renderColumnFilter = (col: InvoiceColumnDef) => {
    if (col.noFilter) return <TableHead key={`filter-${col.key}`} />;

    // Searchable-select filter (combobox with search)
    if (col.filterType === 'searchable-select') {
      const options = searchableSelectOptions?.[col.key] || [];
      return (
        <TableHead key={`filter-${col.key}`} className="p-1">
          <SearchableSelectFilter
            colKey={col.key}
            options={options}
            value={columnFilters[col.key] || ''}
            onChange={onColumnFilterChange}
          />
        </TableHead>
      );
    }

    if (col.filterType === 'single-select') {
      const options = searchableSelectOptions?.[col.key] || [];
      return (
        <TableHead key={`filter-${col.key}`} className="p-1">
          <Select
            value={columnFilters[col.key] || '__all__'}
            onValueChange={(value) =>
              onColumnFilterChange(col.key, value === '__all__' ? '' : value)
            }
          >
            <SelectTrigger
              aria-label={`Filter ${col.label}`}
              className="h-7 w-full min-w-0 text-xs font-normal"
            >
              <SelectValue placeholder="All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All</SelectItem>
              {options.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </TableHead>
      );
    }

    // Multi-select filter
    if (col.filterType === 'select' && col.filterOptions) {
      const selected: string[] = (() => {
        try { return columnFilters[col.key] ? JSON.parse(columnFilters[col.key]) : []; }
        catch { return []; }
      })();

      const toggle = (opt: string) => {
        const next = selected.includes(opt)
          ? selected.filter((o) => o !== opt)
          : [...selected, opt];
        onColumnFilterChange(col.key, next.length ? JSON.stringify(next) : '');
      };

      return (
        <TableHead key={`filter-${col.key}`} className="p-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-7 w-full text-xs justify-start font-normal">
                {selected.length ? `${selected.length} selected` : '—'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-44">
              {col.filterOptions.map((opt) => (
                <DropdownMenuCheckboxItem
                  key={opt.value}
                  checked={selected.includes(opt.value)}
                  onCheckedChange={() => toggle(opt.value)}
                  onSelect={(e) => e.preventDefault()}
                >
                  {opt.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </TableHead>
      );
    }

    // Date range filter
    if (col.filterType === 'date-range') {
      const raw = columnFilters[col.key] || '';
      const [from = '', to = ''] = raw.split('>>');
      return (
        <TableHead key={`filter-${col.key}`} className="p-1">
          <div className="flex flex-col gap-0.5">
            <Input
              type="date"
              className="h-6 text-xs px-1"
              value={from}
              onChange={(e) => onColumnFilterChange(col.key, `${e.target.value}>>${to}`)}
              title="From"
            />
            <Input
              type="date"
              className="h-6 text-xs px-1"
              value={to}
              onChange={(e) => onColumnFilterChange(col.key, `${from}>>${e.target.value}`)}
              title="To"
            />
          </div>
        </TableHead>
      );
    }

    // Number range filter
    if (col.filterType === 'number-range') {
      const raw = columnFilters[col.key] || '';
      const [min = '', max = ''] = raw.split('>>');
      return (
        <TableHead key={`filter-${col.key}`} className="p-1">
          <div className="flex flex-col gap-0.5">
            <Input
              type="number"
              className="h-6 text-xs px-1"
              value={min}
              onChange={(e) => onColumnFilterChange(col.key, `${e.target.value}>>${max}`)}
              placeholder="Min"
            />
            <Input
              type="number"
              className="h-6 text-xs px-1"
              value={max}
              onChange={(e) => onColumnFilterChange(col.key, `${min}>>${e.target.value}`)}
              placeholder="Max"
            />
          </div>
        </TableHead>
      );
    }

    // Text filter (default)
    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <Input
          className="h-7 text-xs"
          value={columnFilters[col.key] || ''}
          onChange={(e) => onColumnFilterChange(col.key, e.target.value)}
          placeholder="..."
        />
      </TableHead>
    );
  };

  // ---------------------------------------------------------------------------
  // Cell rendering
  // ---------------------------------------------------------------------------
  const renderCell = (col: InvoiceColumnDef, invoice: Invoice): React.ReactNode => {
    const raw = col.getValue(invoice);

    switch (col.key) {
      case 'status': {
        const isOverdue =
          invoice.status === 'sent' &&
          invoice.expiration_date &&
          new Date(invoice.expiration_date) < new Date();
        return (
          <div className="flex items-center gap-2">
            <InvoiceStatusBadge status={invoice.status} />
            {isOverdue && (
              <span className="text-xs text-red-600 font-medium">Overdue</span>
            )}
          </div>
        );
      }
      case 'total':
        return formatCurrency(invoice.total ?? 0, invoice.currency || 'CAD');
      case 'subtotal':
        return formatCurrency(
          invoice.subtotal_amount ?? invoice.total ?? 0,
          invoice.currency || 'CAD',
        );
      case 'tax':
        return (invoice.tax_amount ?? 0) > 0
          ? formatCurrency(invoice.tax_amount ?? 0, invoice.currency || 'CAD')
          : '—';
      case 'account':
        return isPopulatedOrganisation(invoice.customer) ? (
          <Link
            to={`/accounts/${invoice.customer._id}`}
            onClick={(e) => e.stopPropagation()}
            className="text-atek-blue hover:text-atek-blue-dark hover:underline"
          >
            {invoice.customer.name || '—'}
          </Link>
        ) : (
          getCustomerName(invoice.customer)
        );
      case 'expirationDate':
        return (
          <span className={getDeadlineClass(invoice.expiration_date, invoice.status)}>
            {raw ? formatDate(String(raw)) : '—'}
          </span>
        );
      case 'nextStepDeadline':
        return (
          <span className={getDeadlineClass(invoice.next_step_deadline, invoice.status)}>
            {raw ? formatDate(String(raw)) : '—'}
          </span>
        );
      case 'documents': {
        const count = invoice.document_count ?? 0;
        if (count === 0) return '—';
        return (
          <span className="inline-flex items-center gap-1 text-muted-foreground" title={`${count} document${count > 1 ? 's' : ''} linked`}>
            <Paperclip className="h-3.5 w-3.5" />
            <span className="text-xs">{count}</span>
          </span>
        );
      }
      case 'actions': {
        const isUnlinked = !!(invoice.pgId && invoice._id === invoice.pgId);
        return (
          <div className="flex items-center gap-1">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <button
                      type="button"
                      disabled={isUnlinked}
                      onClick={(e) => { e.stopPropagation(); onEditClick(invoice); }}
                      className={cn(
                        'text-sm',
                        isUnlinked
                          ? 'text-muted-foreground cursor-not-allowed opacity-50'
                          : 'text-atek-blue hover:underline cursor-pointer',
                      )}
                    >
                      Edit
                    </button>
                  </span>
                </TooltipTrigger>
                {isUnlinked && (
                  <TooltipContent>
                    Awaiting sync — editable after the next sync
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
            {onReassign && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onReassign(invoice); }}
                className="text-muted-foreground hover:text-foreground cursor-pointer"
                title="Reassign to another account"
              >
                <ArrowRightLeft className="h-3.5 w-3.5" />
              </button>
            )}
            {invoice.hubspot_link && (
              <a
                href={invoice.hubspot_link}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-muted-foreground hover:text-primary"
                title="HubSpot"
              >
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
            {invoice.gdrive_link && (
              <a
                href={invoice.gdrive_link}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-muted-foreground hover:text-primary"
                title="Google Drive"
              >
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
          </div>
        );
      }
      default:
        // Auto-format date columns
        if (col.filterType === 'date-range') {
          return raw ? formatDate(String(raw)) : '—';
        }
        return String(raw ?? '') || '—';
    }
  };

  // ---------------------------------------------------------------------------
  // Showing text
  // ---------------------------------------------------------------------------
  const showingText = invoices.length < pageItemCount
    ? `Showing ${invoices.length} of ${pageItemCount} on this page`
    : `Showing ${pageItemCount} of ${total}`;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <>
      {/* Toolbar: showing text + export + column toggle */}
      <div className="flex items-center justify-between px-4 py-2 border-b">
        <span className="text-sm text-muted-foreground">{showingText}</span>
        <div className="flex items-center gap-2">
          {hasActiveColumnFilters && onClearColumnFilters && (
            <Button variant="outline" size="sm" onClick={onClearColumnFilters}>
              <FilterX className="h-4 w-4 mr-1" /> Clear filters
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={handleCopy}>
            <Copy className="h-4 w-4 mr-1" />
            Copy
          </Button>
          <Button variant="outline" size="sm" onClick={handleCSV}>
            <Download className="h-4 w-4 mr-1" />
            CSV
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Settings2 className="h-4 w-4 mr-1" />
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {COLUMNS.filter((c) => !c.noFilter).map((col) => (
                <DropdownMenuCheckboxItem
                  key={col.key}
                  checked={isVisible(col.key)}
                  onCheckedChange={() => toggleColumn(col.key)}
                  onSelect={(e) => e.preventDefault()}
                >
                  {col.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            {/* ROW 1: Filter inputs (always visible, only visible columns) */}
            <TableRow className="bg-muted/30 hover:bg-muted/30">
              {visibleColumns.map((col) => renderColumnFilter(col))}
            </TableRow>

            {/* ROW 2: Column labels + sort controls */}
            <TableRow>
              {visibleColumns.map((col) => (
                <TableHead
                  key={col.key}
                  className={cn(
                    col.headerClassName,
                    col.align === 'right' && 'text-right',
                  )}
                >
                  {col.sortKey ? (
                    <SortableHeader
                      label={col.label}
                      sortKey={col.sortKey}
                      currentSortKey={sortKey}
                      sortDirection={sortDirection}
                      onSort={onSort}
                    />
                  ) : (
                    col.label
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>

          <TableBody>
            {/* Loading state */}
            {isLoading && Array.from({ length: 10 }).map((_, i) => (
              <TableRow key={i}>
                {visibleColumns.map((col) => (
                  <TableCell key={col.key}><Skeleton className="h-4 w-full" /></TableCell>
                ))}
              </TableRow>
            ))}

            {/* Empty state */}
            {!isLoading && invoices.length === 0 && (
              <TableRow>
                <TableCell colSpan={visibleColumns.length} className="py-12 text-center text-muted-foreground">
                  No invoices found
                </TableCell>
              </TableRow>
            )}

            {/* Data rows */}
            {!isLoading && invoices.map((invoice) => (
              <TableRow
                key={invoice._id}
                className="hover:bg-muted/50 cursor-pointer"
                onClick={() => onEditClick(invoice)}
              >
                {visibleColumns.map((col) => (
                  <TableCell
                    key={col.key}
                    className={cn(
                      'text-sm',
                      col.align === 'right' && 'text-right tabular-nums',
                      col.cellClassName,
                    )}
                  >
                    {renderCell(col, invoice)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="border-t px-4 py-3 flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {page + 1} of {totalPages}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => onPageChange(page - 1)} disabled={page === 0}>
              Previous
            </Button>
            <Button variant="outline" size="sm" onClick={() => onPageChange(page + 1)} disabled={page >= totalPages - 1}>
              Next
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
